import React, { useState, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {createSelector} from "reselect";
import {useTranslation, Trans} from "react-i18next";
import {Icon, Menu, MenuItem, Tag} from "@blueprintjs/core";

// Libs
import {boxShadow, primaryColor} from "../common/libs/theme-lib";

// Components
import Button from "../common/components/button";
import ActivateClientDialog from "./activate-client-dialog";
import DeactivateClientDialog from "./deactivate-client-dialog";
import DeleteClientDialog from "./delete-client-dialog";
import NewClientDialogPage from "./new-client-dialog";
import Popover from "../common/components/popover";
import UpdateClientDialog from "./update-client-dialog";
import DocumentationClientDialog from "../documentation/documentation-client-dialog";
import VirtualizedTable from "../common/components/virtualized-table";
import TextInput from "../common/components/text-input";
import useInputValidation from "../common/hooks/use-input-validation";

// Hooks
import {useUserClientsData} from "../user-clients-data/user-clients-data";

// Actions
import {readClients} from "./clients-actions";

// Selectors
import {
    clientsSelector,
    clientsCreatedSelector,
    clientsUpdatedSelector,
    clientsDeletedSelector
} from "./clients-selectors";

// Component Selector
const clientsPageSelector = createSelector(
    clientsSelector,
    clientsCreatedSelector,
    clientsUpdatedSelector,
    clientsDeletedSelector,
    (clients, clientCreated, clientUpdated, clientDeleted) => {
        return {
            clients: clients.toJS(),
            clientCreated,
            clientUpdated,
            clientDeleted
        }
    }
);

// Redux dispatch hook
const useReduxDispatch = () => {
    const dispatch = useDispatch();
    return {
        readClients: () => dispatch(readClients())
    }
};

// Clients popover
const ClientsPopover = (props) => {
    const {client} = props;
    const {t} = useTranslation("clients");

    // update client dialog state
    const [updateClientDialogIsOpen, setUpdateClientDialogIsOpen] = useState(false);

    // deactivate client dialog state
    const [deactivateClientDialogIsOpen, setDeactivateClientDialogIsOpen] = useState(false);

    // activate client dialog state
    const [activateClientDialogIsOpen, setActivateClientDialogIsOpen] = useState(false);

    // documentation client dialog state
    const [documentationClientDialogIsOpen, setDocumentationClientDialogIsOpen] = useState(false);

    // delete client dialog state
    const [deleteClientDialogIsOpen, setDeleteClientDialogIsOpen] = useState(false);

    return (
        <div style={{cursor: "pointer"}}>
            <Popover
                content={(
                    <Menu>
                        <MenuItem
                            text={t("popover.edit")}
                            onClick={() => setUpdateClientDialogIsOpen(true)}
                        />
                        {client.status === "active" && (
                            <MenuItem
                                text={t("popover.deactivate")}
                                onClick={() => setDeactivateClientDialogIsOpen(true)}
                            />
                        )}
                        {client.status === "inactive" && (
                            <MenuItem
                                text={t("popover.activate")}
                                onClick={() => setActivateClientDialogIsOpen(true)}
                            />
                        )}
                        <MenuItem
                            text={t("popover.documentation")}
                            onClick={() => setDocumentationClientDialogIsOpen(true)}
                        />
                        <MenuItem
                            text={t("popover.delete")}
                            onClick={() => setDeleteClientDialogIsOpen(true)}
                            style={{color: "#E2271D"}}
                        />
                    </Menu>
                )}
                position={"bottom-end"}
            >
                <div>
                    <Icon icon="more" iconSize={20}/>
                </div>
            </Popover>
            <UpdateClientDialog
                isOpen={updateClientDialogIsOpen}
                onClose={() => setUpdateClientDialogIsOpen(false)}
                client={client}
            />
            <DeactivateClientDialog
                isOpen={deactivateClientDialogIsOpen}
                onClose={() => setDeactivateClientDialogIsOpen(false)}
                client={client}
            />
            <ActivateClientDialog
                isOpen={activateClientDialogIsOpen}
                onClose={() => setActivateClientDialogIsOpen(false)}
                client={client}
            />
            <DocumentationClientDialog
                isOpen={documentationClientDialogIsOpen}
                onClose={() => setDocumentationClientDialogIsOpen(false)}
                client={client}
            />
            <DeleteClientDialog
                isOpen={deleteClientDialogIsOpen}
                onClose={() => setDeleteClientDialogIsOpen(false)}
                client={client}
            />
        </div>
    );
};

const ClientsPage = (props) => {

    // User clients data hook
    const userClientsData = useUserClientsData();

    // new client dialog state
    const [newClientDialogIsOpen, setNewClientDialogIsOpen] = useState(false);

       
    // Redux
    const {clients, clientCreated, clientUpdated, clientDeleted} = useSelector(state => clientsPageSelector(state));
    const {readClients} = useReduxDispatch();

    // States
    const [clientName, setClientName] = useState("")

    //i18n
    const {t} = useTranslation("clients");

    // show customer list when rendering component
    useEffect(() => {
        readClients();
    }, []);

    useEffect(() => userClientsData.addUserClientData(clients.find(client => client._id === clientCreated)), [clientCreated]);
    useEffect(() => userClientsData.updateUserClientData(clients.find(client => client._id === clientUpdated)), [clientUpdated]);
    useEffect(() => userClientsData.deleteUserClientData(clientDeleted), [clientDeleted]);

    let filterClients = []

    if(!clientName){
        filterClients = clients
    }else{
        filterClients = clients.filter( (client) => client.name.toLowerCase().includes(clientName.toLocaleLowerCase()) )
    }

    const active = "active";
    const inactive = "inactive";
    const countActive = filterClients.reduce((acc, cur) => cur.status === active ? ++acc : acc, 0);
    const countInactive = filterClients.reduce((acc, cur) => cur.status === inactive ? ++acc : acc, 0);

    return (
        <>
            <div style={{
                display: "flex",
                alignItems: "flex-end",
                paddingBottom: "15px"
            }}>
                <div style={{flex: 1}}>
                    <div style={{
                        color: primaryColor,
                        fontSize: 28,
                        fontWeight: "bold"
                    }}>{t("title")}</div>
                    <div style={{fontWeight: 100, fontSize: 18}}>{t("description")}</div>
                </div>
                <div style={{
                    position: "relative",
                }}>
                    <div style={{
                        color: "black",
                        fontWeight: "bold",
                        fontSize: 12,
                        marginBottom: 5
                    }}>
                        {t("clientName")}
                    </div>
                    <input
                        type="text"
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        style={{ marginRight: 75, width: 200 }}
                    />
                </div>
                <Button
                    text={t("common:actions.newClient")}
                    action={() => setNewClientDialogIsOpen(true)}
                    intent="primary"
                />
            </div>
            <div style={{
                backgroundColor: "white",
                boxShadow: boxShadow,
                padding: 15,
                flex: 1,
                display: "flex",
                flexDirection: "column"
            }}>
                <div style={{marginBottom: 15, fontSize: 18, fontWeight: 100}}>
                    <Trans
                        i18nKey="clients:clientsTableHeader"
                        values={{count: filterClients.length, countActive: countActive, countInactive: countInactive}}
                        components={[<strong>number</strong>]}
                    />
                </div>
                <VirtualizedTable
                    config={{
                        sortable: true,
                        showFooter: true
                    }}
                    columns={[{
                        Header: t("clientsTableColumns.client_name"),
                        accessor: "name"
                    }, {
                        Header: t("clientsTableColumns.client_id"),
                        accessor: "sap_id"
                    }, {
                        Header: t("clientsTableColumns.cif"),
                        accessor: "nif"
                    }, {
                        Header: t("clientsTableColumns.date"),
                        accessor: "createdAt",
                        type: "date"
                    }, {
                        Header: t("clientsTableColumns.users_count"),
                        accessor: "users",
                        type: "function",
                        nodeContent: (value) => {
                            if (value === undefined) {
                                return (
                                    <div>
                                        0
                                    </div>
                                );
                            } else {
                                return (
                                    <div>
                                        {value}
                                    </div>
                                );
                            }
                        },
                        style: {
                            textAlign: "right"
                        }
                    }, {
                        Header: t("clientsTableColumns.state"),
                        accessor: "status",
                        type: "function",
                        nodeContent: (value) => {
                            return (
                                <div style={{
                                    position: "absolute",
                                    top: "4px"
                                }}>
                                    {value === "active" && (
                                        <Tag
                                            style={tagStyleAc}
                                            large={true}
                                            intent={"success"}
                                        >
                                            {t("stateTag.active")}
                                        </Tag>
                                    )}
                                    {value === "inactive" && (
                                        <Tag
                                            style={tagStyleIn}
                                            large={true}
                                            intent={"danger"}
                                        >
                                            {t("stateTag.inactive")}
                                        </Tag>
                                    )}
                                </div>
                            );
                        },
                        style: {
                            paddingLeft: "30px"
                        }
                    }, {
                        id: "actions",
                        Header: "",
                        accessor: "_id",
                        type: "function",
                        disableSortBy: true,
                        minWidth: 60,
                        width: 60,
                        style: {
                            maxWidth: 60,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center"
                        },
                        nodeContent: (value) => {
                            return (
                                <ClientsPopover
                                    client={filterClients.find((client) => client._id === value)}
                                />
                            );
                        }
                    }]}
                    data={filterClients}
                />
            </div>
            <NewClientDialogPage
                isOpen={newClientDialogIsOpen}
                onClose={() => setNewClientDialogIsOpen(false)}
            />
        </>
    );
};

export default ClientsPage;

const tagStyleAc = {
    minWidth: "100px",
    textAlign: "center",
    backgroundColor: "#4caf50"
};

const tagStyleIn = {
    minWidth: "100px",
    textAlign: "center",
    backgroundColor: "#e2271d"
};